import { offerKeys } from "./offer-services";
import { useConcatPages } from "../../../../shared/infrastructure";
import { useFetch } from "../../../../public/auth/infrastructure/store/fetch-context";
import { OrderT, UseGetOrdersFilters } from "../../domain";
import {
  PageAttr,
  StatusErrorResponse,
} from "../../../../shared/domain/entities/fetch-entities";
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

const orderKeys = {
  all: ["orders"] as const,
  orders: () => [...orderKeys.all, "orders-list"] as const,
  ordersList: (filters: UseGetOrdersFilters) =>
    [...orderKeys.orders(), filters] as const,
  orderByMovement: (movement: string) =>
    [...orderKeys.all, "movement", movement] as const,
};

const ORDERS_ENDPOINT = "orders";

const useInfiniteGetOrders = (
  filters: UseGetOrdersFilters,
  config: UseInfiniteQueryOptions<PageAttr<OrderT>> = {}
): UseInfiniteQueryResult<PageAttr<OrderT>> & {
  allPages: PageAttr<OrderT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<OrderT>>(
    orderKeys.ordersList(filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<OrderT>>(ORDERS_ENDPOINT, {
        params: {
          page,
          limit: 10,
          ...filters,
        },
      });

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length > 0 ? allPages.length + 1 : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

type GetOrderResponse = {
  data: OrderT;
};

const useGetOrder = (): UseMutationResult<
  GetOrderResponse,
  StatusErrorResponse,
  OrderT["movementNumber"]
> => {
  const fetch = useFetch();

  return useMutation<
    GetOrderResponse,
    StatusErrorResponse,
    OrderT["movementNumber"]
  >(
    async (movementNumber) =>
      (
        await fetch.get<GetOrderResponse>(
          `${ORDERS_ENDPOINT}/${movementNumber}`
        )
      ).data
  );
};

const useGetOrderByMovement = (
  movementNumber: OrderT["movementNumber"],
  options?: UseQueryOptions<GetOrderResponse, StatusErrorResponse>
): UseQueryResult<GetOrderResponse, StatusErrorResponse> => {
  const fetch = useFetch();

  return useQuery<GetOrderResponse, StatusErrorResponse>(
    orderKeys.orderByMovement(movementNumber),
    async () =>
      (
        await fetch.get<GetOrderResponse>(
          `${ORDERS_ENDPOINT}/${movementNumber}`
        )
      ).data,
    options
  );
};

type ZipCodesOrderResponse = {
  data: OrderT;
};

type ZipCodesOrderPayload = {
  movementNumber: OrderT["movementNumber"];
  payload: {
    destination: { zip_code: string };
    origin: { zip_code: string };
  };
};

const useZipCodesOrder = (): UseMutationResult<
  ZipCodesOrderResponse,
  StatusErrorResponse,
  ZipCodesOrderPayload
> => {
  const fetch = useFetch();
  const queryClient = useQueryClient();

  return useMutation<
    ZipCodesOrderResponse,
    StatusErrorResponse,
    ZipCodesOrderPayload
  >(
    async (params) =>
      (
        await fetch.patch<ZipCodesOrderResponse>(
          `${ORDERS_ENDPOINT}/${params.movementNumber}`,
          params.payload
        )
      ).data,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(orderKeys.all);
        await queryClient.invalidateQueries(offerKeys.queueList());
      },
    }
  );
};

export {
  orderKeys,
  useGetOrder,
  useGetOrderByMovement,
  useInfiniteGetOrders,
  useZipCodesOrder,
};
